import { ONE_SECOND_MS } from '@shared/utils/constants/time';
import { HttpStatus } from '../enums';
import { ContentTooLargeError } from '../errors';
import { ExtendedRequestResponseEvent } from '../interfaces';
import { captureSentryMessage } from '@shared/sentry';
import { LogLevel } from '@environment/enums/log-level.enum';

const TIMEOUT_MS = ONE_SECOND_MS * 20;

export async function postToBackend(
  backendUrl: string, 
  payload: ExtendedRequestResponseEvent, 
  requestKey?: string
): Promise<void> {
  const {
    pageUrl,
    request: { url },
    response: { contentType },
  } = payload;
  
  captureSentryMessage(`postToBackend: ${requestKey || 'unknown'}, pageUrl: ${pageUrl}, url: ${url}`, {
    pageUrl,
    url,
    contentType,
  }, LogLevel.Trace);
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
  
  try {
    const response = await fetch(backendUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      mode: 'no-cors',
      signal: controller.signal,
    });
    
    const { status } = response;
    captureSentryMessage(`postToBackend response: key=${requestKey || 'unknown'}, status=${status}`, {
      pageUrl,
      url,
      contentType,
      body: JSON.stringify(payload),
    }, LogLevel.Trace);
    
    if (status === HttpStatus.ContentTooLarge) {
      throw new ContentTooLargeError(
        `Content too large for key: ${requestKey}, page URL: ${pageUrl}, request URL: ${url}, content type: ${contentType}`
      );
    } else if (status >= HttpStatus.BadRequest) {
      throw new Error(`postToBackend got error status: ${status} for key: ${requestKey}`);
    }
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') {
      throw new Error(`Fetch request timed out for key: ${requestKey}`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}
